/**
 * ============================
 * Custom String Options - Created by www.symmetrycommerce.com 
 * ============================
 * Handles:
 * - Main and Cross string pickers
 * - Unstrung, Standard, and Hybrid selection
 * - Dynamic showing/hiding of pickers based on selection
 * - Tension selectors
 * - Hidden Shopify product form properties
 * - Loading indicators for string-picker
 */

// Custom element for string-picker
if (!customElements.get('string-picker')) {
  customElements.define('string-picker', class extends HTMLElement {
    constructor() { super(); }

    async connectedCallback() {
      this.select = this.querySelector('.string-picker__select');
      this.collectionHandle = this.dataset.collectionHandle;
      this.optionType = this.dataset.optionType;
      this.isHybridMode = this.isHybridMode || false;
      if (!this.select) return;

      try {
        const response = await fetch(`/pages/company-information?view=strings-data-template`);
        const data = await response.json();

        const collectionData = data.collections.find(c => c.collection === this.collectionHandle);
        this.products = collectionData ? collectionData.products : [];

        this.populateOptions();
      } catch (err) {
        console.error('Error loading string picker data:', err);
        this.select.innerHTML = '<option>Error loading strings</option>';
        this.select.setAttribute('aria-busy', 'false');
        this.select.setAttribute('aria-invalid', 'true');
        
        // Announce error to screen readers
        const errorMsg = this.querySelector('.error-message') || document.createElement('span');
        errorMsg.className = 'error-message visually-hidden';
        errorMsg.setAttribute('role', 'alert');
        errorMsg.textContent = 'Failed to load string options. Please refresh the page.';
        if (!this.querySelector('.error-message')) {
          this.appendChild(errorMsg);
        }
      }
    }

    populateOptions() {
      const currentValue = this.select.value;
      this.select.innerHTML = ''; // Clear loading
      this.select.setAttribute('aria-busy', 'false');

      // Placeholder option (selected by default)
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.innerText = 'Select String';
      placeholderOption.selected = true;
      this.select.appendChild(placeholderOption);
      
      // Remove loading message
      const loadingMsg = this.querySelector('#main_string_loading, #cross_string_loading');
      if (loadingMsg) {
        loadingMsg.remove();
      }

      const sortedProducts = this.products.sort((a, b) => (parseFloat(b.discount_percentage) || 0) - (parseFloat(a.discount_percentage) || 0));
      sortedProducts.forEach(product => {
        const option = document.createElement('option');
        option.value = product.title;
        option.dataset.id = product.variant_id;
        
        // Get prices from template data (price_in_cents is the current price, original_price is full price)
        const currentPriceInCents = product.price_in_cents || 0;
        const originalPriceInCents = product.original_price || currentPriceInCents;
        
        let displayPrice, totalDiscountPercentage;
        
        if (this.isHybridMode) {
          // Hybrid: 50% off the current displayed price
          const hybridPriceInCents = Math.round(currentPriceInCents / 2);
          displayPrice = this.formatPrice(hybridPriceInCents);
          // Total discount = how much off from original price
          totalDiscountPercentage = originalPriceInCents > 0 
            ? Math.round((1 - hybridPriceInCents / originalPriceInCents) * 100) 
            : 0;
        } else {
          // Standard: use the price as-is from template
          displayPrice = product.price;
          totalDiscountPercentage = parseFloat(product.discount_percentage) || 0;
        }
        
        option.dataset.discount = totalDiscountPercentage;
        option.innerText = `${displayPrice} ${product.title}`;
        this.select.appendChild(option);
      });

      // Restore selection if possible, otherwise keep placeholder selected
      if (currentValue) {
        const optionExists = Array.from(this.select.options).some(opt => opt.value === currentValue);
        if (optionExists) {
          this.select.value = currentValue;
        }
      }

      // Sync hidden inputs
      const hiddenInput = document.querySelector(`input[name="properties[${this.dataset.inputName}]"]`);
      const hiddenVariantInput = document.querySelector(`input[name="properties[${this.dataset.inputName}_variant_id]"]`);
      const hiddenDiscountInput = document.querySelector(`input[name="properties[${this.dataset.inputName}_discount]"]`);
      if (hiddenInput) {
        hiddenInput.value = this.select.value;
        const selectedOption = this.select.options[this.select.selectedIndex];
        if (hiddenVariantInput) hiddenVariantInput.value = selectedOption?.dataset.id || '';
        if (hiddenDiscountInput) hiddenDiscountInput.value = selectedOption?.dataset.discount || '0';
      }
    }

    formatPrice(cents) {
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    setHybridMode(isHybrid) {
      if (this.isHybridMode !== isHybrid) {
        this.isHybridMode = isHybrid;
        if (this.products && this.products.length > 0) {
          this.populateOptions();
        }
      }
    }
  });

}

// Initialize Custom String Options
function initializeCustomStringOptions() {

  const SELECTORS = {
    productForm: 'product-form.product-form form',
    mainPicker: 'custom_string_picker_main',
    mainSelect: 'select',
    mainTensionSelect: 'main_string_tension_select',
    container: 'custom_product_options_select_container',
    radios: 'input[name="custom_product_options"]',
    dropdown: 'select[name="custom_product_options"]',
    mainSelect: 'select',
    crossInput: 'input[name="properties[_custom_string_cross]"]',
    crossVariantInput: 'input[name="properties[_custom_string_cross_variant_id]"]',
    crossDiscountInput: 'input[name="properties[_custom_string_cross_discount]"]',
  }

  const IDS = {
    productForm: 'product-form.product-form form',
    mainPicker: 'custom_string_picker_main',
    mainTensionSelect: 'main_string_tension_select',
    mainTensionWrapper: 'main_string_tension_wrapper',
    container: 'custom_product_options_select_container',
    crossPicker: 'custom_string_picker_cross',
    crossTensionWrapper: 'cross_string_tension_wrapper',
  }
  
  const productForm = document.querySelector(SELECTORS.productForm);
  if (!productForm) return;

  const container = document.getElementById(IDS.container);
  const radios = document.querySelectorAll(SELECTORS.radios);
  const dropdown = document.querySelector(SELECTORS.dropdown);

  // Store hidden inputs references
  let mainInput, mainVariantInput, mainDiscountInput, mainTensionInput;

  // Handle Standard vs Hybrid vs Unstrung changes (both radio and dropdown)
  if (radios.length > 0) {
    // Radio button mode
    radios.forEach(radio => {
      radio.addEventListener('change', handleRadioChange);
    });
    // Initialize on load - check which radio is checked
    const checkedRadio = document.querySelector('input[name="custom_product_options"]:checked');
    if (checkedRadio) {
      handleRadioChange({ target: checkedRadio });
    }
  } else if (dropdown) {
    // Dropdown mode
    dropdown.addEventListener('change', handleDropdownChange);
    // Initialize on load
    handleDropdownChange({ target: dropdown });
  }

  function handleRadioChange(e) {
    if (!e) return;
    const value = e.target.value;
    
    if (value === 'unstrung') {
      removeMainPicker();
      removeCrossPicker();
    } else if (value === 'standard') {
      addMainPicker(false);
      removeCrossPicker();
      updatePickersHybridMode(false);
    } else if (value === 'hybrid') {
      addMainPicker(true);
      addCrossPicker(true);
      updatePickersHybridMode(true);
    }
  }

  function handleDropdownChange(e) {
    if (!e) return;
    const value = e.target.value;
    
    if (value === 'unstrung') {
      removeMainPicker();
      removeCrossPicker();
    } else if (value === 'standard') {
      addMainPicker(false);
      removeCrossPicker();
      updatePickersHybridMode(false);
    } else if (value === 'hybrid') {
      addMainPicker(true);
      addCrossPicker(true);
      updatePickersHybridMode(true);
    }
  }

  function updatePickersHybridMode(isHybrid) {
    // Update main picker
    const mainPicker = document.getElementById(IDS.mainPicker);
    if (mainPicker) {
      if (mainPicker.setHybridMode) {
        mainPicker.setHybridMode(isHybrid);
      }
      // Swap the main string picker label based on mode
      const mainPickerLabel = mainPicker.querySelector('label');
      if (mainPickerLabel) {
        mainPickerLabel.innerText = getMainStringLabel(isHybrid);
      }
    }
    // Update cross picker
    const crossPicker = document.getElementById(IDS.crossPicker);
    if (crossPicker && crossPicker.setHybridMode) {
      crossPicker.setHybridMode(isHybrid);
    }
    // Update main tension label + rename its hidden input so the cart property key matches
    const mainTensionWrapper = document.getElementById(IDS.mainTensionWrapper);
    if (mainTensionWrapper) {
      const newTensionLabel = getMainTensionLabel(isHybrid);
      const oldTensionLabel = getMainTensionLabel(!isHybrid);

      const tensionLabelEl = mainTensionWrapper.querySelector('label');
      if (tensionLabelEl) {
        tensionLabelEl.innerText = newTensionLabel;
      }
      const oldTensionInput = productForm.querySelector(`input[name="properties[${oldTensionLabel}]"]`);
      if (oldTensionInput) {
        oldTensionInput.name = `properties[${newTensionLabel}]`;
        mainTensionInput = oldTensionInput;
      }
    }
  }

  function removeMainPicker() {
    const mainPicker = document.getElementById(IDS.mainPicker);
    const mainTension = document.getElementById(IDS.mainTensionWrapper);
    
    if (mainPicker) {
      const mainInputs = productForm.querySelectorAll('input[name="properties[_custom_string_main]"], input[name="properties[_custom_string_main_variant_id]"], input[name="properties[_custom_string_main_discount]"]');
      mainInputs.forEach(input => input.remove());
      mainPicker.remove();
    }
    
    if (mainTension) {
      // Input name could be either the hybrid or the standard-mode tension label
      const candidateNames = [getMainTensionLabel(true), getMainTensionLabel(false)];
      candidateNames.forEach(name => {
        const input = productForm.querySelector(`input[name="properties[${name}]"]`);
        if (input) input.remove();
      });
      mainTension.remove();
    }
  }

  function addMainPicker(isHybrid = false) {
    // Check if main picker already exists
    if (document.getElementById(IDS.mainPicker)) return;
    
    // Get collection handle from the container or initial picker
    const collectionHandle = container.dataset.collectionHandle || 'strings';
    
    // Create main string picker
    const mainPicker = createStringPicker('custom_string_picker_main', '_custom_string_main', collectionHandle, isHybrid);
    container.appendChild(mainPicker);
    
    // Create main tension dropdown
    const mainTensionWrapper = createMainTensionDropdown(isHybrid);
    container.appendChild(mainTensionWrapper);
  }

  function getMainTensionLabel(isHybrid) {
    return isHybrid
      ? (container.dataset.mainStringTensionLabel || 'Main String Tension')
      : (container.dataset.standardMainStringTensionLabel || 'Mains and Crosses Tension');
  }

  function getMainStringLabel(isHybrid) {
    return isHybrid
      ? (container.dataset.mainStringLabel || 'Main String')
      : (container.dataset.standardMainStringLabel || 'Mains and Crosses String');
  }

  function createMainTensionDropdown(isHybrid = false) {
    const wrapper = document.createElement('div');
    wrapper.id = IDS.mainTensionWrapper;
    wrapper.className = 'string-tension-wrapper';

    const tensionLabel = getMainTensionLabel(isHybrid);

    const label = document.createElement('label');
    label.setAttribute('for', 'main_string_tension_select');
    label.innerText = tensionLabel;
    wrapper.appendChild(label);

    const select = document.createElement('select');
    select.id = 'main_string_tension_select';
    select.className = 'string-tension__select';
    select.setAttribute('aria-required', 'true');

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.innerText = 'Select Tension';
    select.appendChild(defaultOption);

    const tensionsArray = JSON.parse(container.dataset.tensions || '[]');
    tensionsArray.forEach(tension => {
      const option = document.createElement('option');
      option.value = tension;
      option.innerText = tension;
      select.appendChild(option);
    });

    wrapper.appendChild(select);

    mainTensionInput = createHiddenInput(tensionLabel, '');
    productForm.appendChild(mainTensionInput);

    select.addEventListener('change', () => {
      mainTensionInput.value = select.value;
    });

    return wrapper;
  }

  function addCrossPicker(isHybrid = true) {
    // Check if cross picker already exists
    if (document.getElementById(IDS.crossPicker)) return;
    
    // Get collection handle from the container
    const collectionHandle = container.dataset.collectionHandle || 'strings';
    
    // Create cross picker (always hybrid since cross picker only shows in hybrid mode)
    const crossPicker = createStringPicker('custom_string_picker_cross', '_custom_string_cross', collectionHandle, isHybrid);
    container.appendChild(crossPicker);
    
    // Create cross tension
    const crossTensionWrapper = createCrossTensionDropdown();
    container.appendChild(crossTensionWrapper);
  }

  function removeCrossPicker() {
    const crossPicker = document.getElementById(IDS.crossPicker);
    const crossTension = document.getElementById(IDS.crossTensionWrapper);
    if (crossPicker) {
      const hiddenInput = productForm.querySelector(SELECTORS.crossInput);
      const hiddenVariantInput = productForm.querySelector(SELECTORS.crossVariantInput);
      const hiddenDiscountInput = productForm.querySelector(SELECTORS.crossDiscountInput);
      if (hiddenInput) hiddenInput.remove();
      if (hiddenVariantInput) hiddenVariantInput.remove();
      if (hiddenDiscountInput) hiddenDiscountInput.remove();
      crossPicker.remove();
    }
    if (crossTension) {
      const crossTensionLabel = container.dataset.crossStringTensionLabel || 'Cross String Tension';
      const crossTensionInput = productForm.querySelector(`input[name="properties[${crossTensionLabel}]"]`);
      if (crossTensionInput) crossTensionInput.remove();
      crossTension.remove();
    }
  }

  function createHiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    return input;
  }

  function createCrossTensionDropdown() {
    const wrapper = document.createElement('div');
    wrapper.id = IDS.crossTensionWrapper;
    wrapper.className = 'string-tension-wrapper';

    const label = document.createElement('label');
    label.setAttribute('for', 'cross_string_tension_select');
    label.innerText = container.dataset.crossStringTensionLabel || 'Cross String Tension';
    wrapper.appendChild(label);

    const select = document.createElement('select');
    select.id = 'cross_string_tension_select';
    select.className = 'string-tension__select';
    select.setAttribute('aria-required', 'true');

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.innerText = 'Select Tension';
    select.appendChild(defaultOption);

    const tensionsArray = JSON.parse(container.dataset.tensions || '[]');
    tensionsArray.forEach(tension => {
      const option = document.createElement('option');
      option.value = tension;
      option.innerText = tension;
      select.appendChild(option);
    });

    wrapper.appendChild(select);

    const crossTensionInput = createHiddenInput(container.dataset.crossStringTensionLabel || 'Cross String Tension', '');
    productForm.appendChild(crossTensionInput);

    select.addEventListener('change', () => {
      crossTensionInput.value = select.value;
    });

    return wrapper;
  }

  function createStringPicker(id, inputName, collectionHandle, isHybrid = false) {
    const picker = document.createElement('string-picker');
    picker.id = id;
    picker.dataset.inputName = inputName;
    picker.dataset.collectionHandle = collectionHandle;
    picker.isHybridMode = isHybrid;

    const isCross = id === 'custom_string_picker_cross';
    const selectId = isCross ? 'cross_string_select' : 'main_string_select';
    const loadingId = isCross ? 'cross_string_loading' : 'main_string_loading';

    const label = document.createElement('label');
    label.setAttribute('for', selectId);
    if (isCross) {
      label.innerText = container.dataset.crossStringLabel || 'Cross String';
    } else {
      // Main picker: show combined label in Standard mode, regular label in Hybrid mode
      label.innerText = isHybrid
        ? (container.dataset.mainStringLabel || 'Main String')
        : (container.dataset.standardMainStringLabel || 'Mains and Crosses String');
    }
    picker.appendChild(label);

    const select = document.createElement('select');
    select.id = selectId;
    select.classList.add('string-picker__select');
    select.setAttribute('aria-busy', 'true');
    select.setAttribute('aria-describedby', loadingId);
    select.innerHTML = '<option>Loading...</option>';
    picker.appendChild(select);

    // Add loading message for screen readers
    const loadingMsg = document.createElement('span');
    loadingMsg.id = loadingId;
    loadingMsg.className = 'visually-hidden';
    loadingMsg.textContent = 'Loading string options';
    picker.appendChild(loadingMsg);

    const hiddenInput = createHiddenInput(inputName, '');
    const hiddenVariantInput = createHiddenInput(inputName + '_variant_id', '');
    const hiddenDiscountInput = createHiddenInput(inputName + '_discount', '');
    productForm.appendChild(hiddenInput);
    productForm.appendChild(hiddenVariantInput);
    productForm.appendChild(hiddenDiscountInput);

    select.addEventListener('change', () => {
      hiddenInput.value = select.value;
      const selectedOption = select.options[select.selectedIndex];
      hiddenVariantInput.value = selectedOption.dataset.id || '';
      hiddenDiscountInput.value = selectedOption.dataset.discount || '0';
    });

    return picker;
  }
}

document.addEventListener('DOMContentLoaded', initializeCustomStringOptions)
